import { getAmplitudeClient } from '@/features/amplitude';
import { Icon, TId } from '@/features/common';
import { useNotifications } from '@/features/notifications';
import { useGenericPricingUpsellModalContext } from '@/features/upsell';
import { usePlanLimits } from '@/hooks/planLimits';
import { useGenerateJobDescriptionMutation } from '@/queries/jobopenings/mutations';
import styled from '@emotion/styled';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { DSButton } from '@hundred5/design-system';
import { useFormikContext } from 'formik';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';

interface EditorActionsProps {
  workspaceId: TId;
  jobOpeningId: TId;
  onEditorUpdated: (description: string) => void;
}

export function EditorActions({
  workspaceId,
  jobOpeningId,
  onEditorUpdated,
}: EditorActionsProps) {
  const { addNotification } = useNotifications();
  const { openPricingUpsell } = useGenericPricingUpsellModalContext();
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  const canAccessAiFeatures = !!usePlanLimits()('ai_features');
  const form = useFormikContext<any>();
  const generateJobDescription = useGenerateJobDescriptionMutation();
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    if (form.submitCount === 0) {
      setGenerated(false);
    }
  }, [form.submitCount]);

  const handleGenerateDescription = () => {
    if (!canAccessAiFeatures) {
      openPricingUpsell('generic');
      return;
    }

    generateJobDescription.mutate(
      {
        workspaceId: Number(workspaceId),
        openingId: Number(jobOpeningId),
        data: {
          name: form.values.jobTitle,
          employmentType: form.values.employmentType,
          department: form.values.department || undefined,
          workplaceType: form.values.workplaceType || undefined,
          locationAreas:
            form.values.workplaceType === 'remote' &&
            form.values.locationAreas.length
              ? form.values.locationAreas
              : undefined,
          locationCity:
            form.values.workplaceType !== 'remote' && form.values.locationCity
              ? form.values.locationCity
              : undefined,
          salaryCurrency: form.values.salaryCurrency,
          salaryMax: form.values.salaryMax || undefined,
          salaryMin: form.values.salaryMin || undefined,
        },
      },
      {
        onSuccess: (data) => {
          setGenerated(true);
          onEditorUpdated(data.description);

          amplitude?.logEvent(`job description/generate with ai`, {
            'job opening id': jobOpeningId,
          });
          posthog?.capture(`hire_admin_app:job description/generate with ai`, {
            'job opening id': jobOpeningId,
          });
        },
        onError: () => {
          // TODO: temporary error handling
          addNotification({ type: 'bad_request' });
        },
      }
    );
  };

  const handleClearDescription = () => {
    setGenerated(false);
    onEditorUpdated('');

    amplitude?.logEvent(`job description/clear draft`, {
      'job opening id': jobOpeningId,
    });
    posthog?.capture(`hire_admin_app:job description/clear draft`, {
      'job opening id': jobOpeningId,
    });
  };

  return (
    <Actions>
      {generated ? (
        <>
          <Disclaimer>
            Note: AI can make mistakes, please check details of the job
            description
          </Disclaimer>
          <DSButton
            size="small"
            variant="secondary"
            onClick={handleClearDescription}
          >
            Clear draft
          </DSButton>
        </>
      ) : (
        <DSButton
          size="small"
          variant="secondary"
          onClick={handleGenerateDescription}
          disabled={generateJobDescription.isPending}
        >
          <Icon icon={regular('wand-magic-sparkles')} />
          {generateJobDescription.isPending
            ? 'Generating...'
            : 'Generate using AI'}
        </DSButton>
      )}
    </Actions>
  );
}

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
  padding: 4px 12px 8px;
  gap: 8px;
`;

const Disclaimer = styled.span`
  margin-right: auto;
  color: ${({ theme }) => theme.typography.colorSecondary};
  font-size: 12px;
  font-style: italic;
`;
